import React from 'react';
import { FaCheckCircle, FaEye, FaEyeSlash, FaLayerGroup } from 'react-icons/fa';
import { getRevealMeta } from '../opening/layeredGift/moduleRevealMeta';
import { TEXTURE_OPTIONS } from '../opening/layeredGift/moduleRevealMeta';
import { GLOBAL_REVEAL_STYLE_OPTIONS } from '../opening/layeredGift/revealRegistry';
import './LayeredRevealSettingsCard.css';

const STYLE_LABELS = {
  envelope_flap: 'Envelope Flap',
  paper_rise: 'Paper Rise',
  handwritten_card: 'Handwritten Card',
  wave_capsule: 'Wave Capsule',
  postcard_player: 'Postcard Player',
  photo_stack: 'Photo Stack',
  film_strip: 'Film Strip',
  gallery_cards: 'Gallery Cards',
  vertical_ribbon: 'Vertical Ribbon',
  film_timeline: 'Film Timeline',
  note_cards: 'Note Cards',
  guest_card: 'Guest Card',
  sticky_notes: 'Sticky Notes',
  foil_card: 'Foil Card',
  final_card: 'Final Card',
  record_card: 'Record Card',
  music_card: 'Music Card',
  split_slide: 'Split Slide',
  folded_map: 'Folded Map',
  campus_map: 'Campus Map',
  venue_route: 'Venue Route',
  glass_case: 'Glass Case',
  archive_shelf: 'Archive Shelf',
  camera_print: 'Camera Print',
  snapshot_stack: 'Snapshot Stack',
  paper_studio: 'Paper Studio',
  light_studio: 'Light Studio',
  gift_layer: 'Gift Layer',
  soft_fade: 'Soft Fade',
  ribbon_unwrap: 'Ribbon Unwrap',
  spotlight_lift: 'Spotlight Lift',
  card_peek: 'Card Peek',
  cinematic_slide: 'Cinematic Slide',
};

const TEXTURE_LABELS = {
  none: 'None',
  paper: 'Paper',
  map: 'Map',
  archive: 'Archive',
  linen: 'Linen',
  vellum: 'Vellum',
  film: 'Film',
  champagne: 'Champagne',
  botanical: 'Botanical',
};

const humanize = (value = '') => STYLE_LABELS[value] || String(value || 'Default')
  .split('_')
  .filter(Boolean)
  .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
  .join(' ');

const MiniPreview = ({ tone }) => (
  <span className="lumirae-layer-settings-preview" data-tone={tone || 'gift'} aria-hidden="true">
    <span className="preview-stage">
      <i className="preview-piece preview-piece-a" />
      <i className="preview-piece preview-piece-b" />
      <i className="preview-piece preview-piece-c" />
    </span>
  </span>
);

const LayeredRevealSettingsCard = ({
  module,
  moduleName,
  moduleIndex = 0,
  theme,
  isComplete = false,
  onUpdateModuleConfig,
}) => {
  if (!module) return null;

  const revealMeta = getRevealMeta(module, moduleIndex, { themeId: theme?.id });
  const RevealIcon = revealMeta.Icon || FaLayerGroup;
  const moduleId = module.id || module._localId;
  const isInFlow = module?.data?.showInLayeredGift !== false;
  const revealStyleOptions = Array.from(new Set([
    ...(revealMeta.supportedRevealStyles || ['gift_layer']),
    ...GLOBAL_REVEAL_STYLE_OPTIONS,
  ]));

  const updateConfig = (patch) => {
    if (!moduleId) return;
    onUpdateModuleConfig?.(moduleId, patch);
  };

  return (
    <div
      className="lumirae-layer-settings-card"
      data-ready={isComplete ? 'true' : 'false'}
      data-in-flow={isInFlow ? 'true' : 'false'}
    >
      <div className="lumirae-layer-settings-head">
        <div className="lumirae-layer-settings-icon" style={{ color: theme?.accent }}>
          <RevealIcon />
        </div>
        <div className="min-w-0 flex-1">
          <div className="lumirae-layer-settings-kicker">Layer-by-layer config</div>
          <div className="lumirae-layer-settings-title">{moduleName || revealMeta.moduleTitle}</div>
        </div>
        <span
          className="lumirae-layer-settings-status"
          data-ready={isComplete ? 'true' : 'false'}
        >
          {isComplete && <FaCheckCircle />}
          {isComplete ? 'Ready' : 'Missing'}
        </span>
      </div>

      <div className="lumirae-layer-settings-preview-row">
        <MiniPreview tone={revealMeta.tone} />
        <div className="lumirae-layer-settings-summary">
          <span>Reveal: {humanize(revealMeta.revealStyle)}</span>
          <small>{revealMeta.copy}</small>
        </div>
      </div>

      <div className="lumirae-layer-settings-controls">
        <label>
          <span>Reveal Style</span>
          <select
            value={revealMeta.revealStyle}
            onChange={(event) => updateConfig({ revealStyle: event.target.value })}
          >
            {revealStyleOptions.map((style) => (
              <option key={style} value={style}>{humanize(style)}</option>
            ))}
          </select>
        </label>

        <label>
          <span>Texture</span>
          <select
            value={revealMeta.texture || 'none'}
            onChange={(event) => updateConfig({ texture: event.target.value })}
          >
            {TEXTURE_OPTIONS.map((texture) => (
              <option key={texture} value={texture}>{TEXTURE_LABELS[texture] || humanize(texture)}</option>
            ))}
          </select>
        </label>
      </div>

      <button
        type="button"
        className="lumirae-layer-settings-flow-toggle"
        onClick={() => updateConfig({ showInLayeredGift: !isInFlow })}
        aria-pressed={isInFlow}
      >
        {isInFlow ? <FaEye /> : <FaEyeSlash />}
        {isInFlow ? 'Shown in Layer-by-layer flow' : 'Hidden from Layer-by-layer flow'}
      </button>
    </div>
  );
};

export default LayeredRevealSettingsCard;
